import { useState } from "react";
import { PhoneIncoming, PhoneOutgoing, ChevronDown, ChevronUp, Clock, User, AlertCircle, Trash2 } from "lucide-react";
import { formatDate } from "../lib/constants";
import { FollowUpList } from "./FollowUpCard";

// Etichette italiane per gli stati Vapi e per gli endedReason più comuni
const STATO_LABEL = { queued: "In coda", ringing: "Squilla", "in-progress": "In corso", forwarding: "Inoltro", ended: "Terminata" };
const ESITO_LABEL = {
  "customer-ended-call": "Chiusa dal cliente",
  "assistant-ended-call": "Chiusa dall'assistente",
  "assistant-said-end-call-phrase": "Chiusa dall'assistente",
  "customer-did-not-answer": "Nessuna risposta",
  "customer-busy": "Occupato",
  voicemail: "Segreteria",
  "silence-timed-out": "Silenzio prolungato",
  "exceeded-max-duration": "Durata massima superata",
  "assistant-error": "Errore assistente",
  "call.start.error-get-transport": "Errore avvio chiamata (linea/Twilio)",
  "call.start.error-get-phone-number": "Errore numero chiamante",
  "call.start.error-invalid-number": "Numero non valido",
  "call.start.error-no-credit": "Credito insufficiente",
  "nessun-aggiornamento-da-vapi": "Nessun aggiornamento da Vapi",
  "pipeline-error-openai-llm-failed": "Errore LLM",
  "pipeline-error-eleven-labs-voice-failed": "Errore voce ElevenLabs",
  "pipeline-error-deepgram-transcriber-failed": "Errore trascrizione",
};
// Esiti tecnici non mappati: rende leggibile il codice Vapi
const esitoLabel = (e) => ESITO_LABEL[e] || e?.replace(/^call\.start\./, "Avvio: ").replace(/^pipeline-error-/, "Errore: ").replace(/-/g, " ");

const fmtDur = (s) => (s == null ? "—" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);

export const VoiceCallCard = ({ call, showOwner, onDelete }) => {
  const [open, setOpen] = useState(false);
  const inbound = call.direzione === "inbound";
  const active = call.stato !== "ended";
  const Icon = inbound ? PhoneIncoming : PhoneOutgoing;
  const hasDetail = call.trascrizione || call.turni?.length > 0 || call.recording_url || call.riassunto;

  return (
    <li data-testid={`call-card-${call.call_id}`} className={`surface p-5 ${call.errore ? "border-red-500/30" : ""}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${inbound ? "bg-teal-500/15 text-teal-300" : "bg-primary/15 text-primary"}`}>
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <p className="flex flex-wrap items-center gap-2 font-semibold">
              <span data-testid="call-numero">{call.numero || "Numero sconosciuto"}</span>
              {call.nome && <span className="text-sm font-normal text-muted-foreground">· {call.nome}</span>}
              <span data-testid="call-direzione" className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${inbound ? "bg-teal-500/15 text-teal-200" : "bg-primary/15 text-primary"}`}>
                {inbound ? "In entrata" : "In uscita"}
              </span>
            </p>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
              <span>{formatDate(call.created_at)}</span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDur(call.durata_sec)}</span>
              {showOwner && call.user_email && <span className="inline-flex items-center gap-1"><User className="h-3 w-3" /> {call.user_email}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <div className="text-right">
            <span data-testid="call-stato" className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${call.errore ? "bg-red-500/15 text-red-200" : active ? "bg-amber-500/15 text-amber-200" : "bg-secondary text-muted-foreground"}`}>
              {active && <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-amber-300" />}
              {call.errore && <AlertCircle className="h-3.5 w-3.5" />}
              {call.errore ? "Errore" : STATO_LABEL[call.stato] || call.stato}
            </span>
            {call.esito && <p data-testid="call-esito" className={`mt-1 text-[11px] ${call.errore ? "text-red-300" : "text-muted-foreground"}`}>{esitoLabel(call.esito)}</p>}
          </div>
          {onDelete && (
            <button data-testid={`call-delete-${call.call_id}`} onClick={() => onDelete(call)} title="Elimina chiamata" className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-red-500/15 hover:text-red-300">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {call.riassunto && <p data-testid="call-riassunto" className="mt-4 rounded-lg border border-border bg-background/60 p-3 text-sm leading-relaxed">{call.riassunto}</p>}
      {!active && <div className="mt-4"><FollowUpList origineId={call.call_id} compact /></div>}
      {call.note && <p className="mt-2 text-xs text-muted-foreground">Note: {call.note}</p>}

      {call.recording_url && (
        <audio data-testid="call-audio" controls preload="none" src={call.recording_url} className="mt-4 h-10 w-full rounded-full" />
      )}

      {hasDetail && (call.turni?.length > 0 || call.trascrizione) && (
        <div className="mt-3">
          <button data-testid="call-toggle-transcript" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
            {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />} {open ? "Nascondi trascrizione" : "Mostra trascrizione"}
          </button>
          {open && (
            <div data-testid="call-transcript" className="mt-3 space-y-2">
              {call.turni?.length > 0 ? (
                call.turni.map((t, i) => (
                  <div key={i} className={`flex ${t.ruolo === "assistente" ? "justify-start" : "justify-end"}`}>
                    <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${t.ruolo === "assistente" ? "bg-primary/15" : "bg-secondary"}`}>
                      <p className="mb-0.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{t.ruolo === "assistente" ? "Assistente AI" : "Cliente"}</p>
                      {t.testo}
                    </div>
                  </div>
                ))
              ) : (
                <pre className="whitespace-pre-wrap rounded-lg bg-background/60 p-3 font-sans text-sm leading-relaxed">{call.trascrizione}</pre>
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
};
